import React from 'react';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { motion } from 'framer-motion';
import { Select } from './Select';

/**
 * Unified Pagination Component
 * 
 * Supports two modes:
 * 1. **Page-number mode** (Firestore): Provide `totalPages` and `totalItems`
 * 2. **Cursor mode** (HubSpot API): Provide `hasNextPage`/`hasPrevPage` + `onNextCursor`/`onPrevCursor`
 * 
 * Always sticky at the bottom of its scroll container by default.
 */
export default function Pagination({ 
    currentPage = 1, 
    totalPages: totalPagesProp, 
    onPageChange, 
    pageSize = 100, 
    onPageSizeChange,
    totalItems,
    pageSizeOptions = [25, 50, 100, 200, 500, 1000],
    // Cursor-based pagination (for APIs like HubSpot)
    hasNextPage,
    hasPrevPage,
    onNextCursor,
    onPrevCursor,
    // Sticky behavior
    sticky = true
}) {
    const isCursorMode = hasNextPage !== undefined || hasPrevPage !== undefined;

    // Auto-compute totalPages from totalItems when not explicitly provided
    const totalPages = totalPagesProp || 
        (totalItems != null ? Math.max(1, Math.ceil(totalItems / pageSize)) : 1);

    // Page-number mode: visible pages logic
    const pages = Array.from({ length: Math.max(1, totalPages) }, (_, i) => i + 1);
    const visiblePages = pages.filter(p => {
        if (totalPages <= 7) return true;
        if (p === 1 || p === totalPages) return true;
        return Math.abs(p - currentPage) <= 1;
    });

    const calculatedStartItem = (currentPage - 1) * pageSize + 1;
    const startItem = totalItems !== undefined ? Math.min(calculatedStartItem, totalItems === 0 ? 0 : totalItems) : calculatedStartItem;
    const endItem = Math.min(currentPage * pageSize, totalItems || (currentPage * pageSize));

    return (
        <div className={`flex items-center justify-between px-4 py-1.5 border-t border-[color:var(--border)] bg-[color:var(--bg-surface)] text-[color:var(--text-secondary)] text-xs shrink-0 ${sticky ? 'sticky bottom-0 z-10' : ''}`}>
            {/* Left side: Showing X-Y of Z and Page Size */}
            <div className="flex items-center gap-4">
                {totalItems !== undefined && (
                    <div className="flex items-center gap-1">
                        <span className="opacity-70">Showing</span>
                        <span className="text-[color:var(--text-primary)] font-medium">
                            {totalItems === 0 ? 0 : startItem}-{endItem}
                        </span>
                        <span className="opacity-70">of</span>
                        <span className="text-[color:var(--text-primary)] font-medium">{totalItems}</span>
                    </div>
                )}

                {/* Cursor mode: show page info without total */}
                {isCursorMode && totalItems === undefined && (
                    <div className="flex items-center gap-1">
                        <span className="opacity-70">Page</span>
                        <span className="text-[color:var(--text-primary)] font-medium">{currentPage}</span>
                    </div>
                )}
                
                {(totalItems !== undefined || isCursorMode) && onPageSizeChange && (
                    <div className="w-[1px] h-3 bg-[color:var(--border)]" />
                )}

                {onPageSizeChange && (
                    <div className="flex items-center gap-2">
                        <span className="opacity-70">Rows:</span>
                        <Select 
                            value={pageSize} 
                            onChange={onPageSizeChange}
                            options={pageSizeOptions.map(size => ({ label: String(size), value: size }))}
                            variant="ghost"
                            size="sm"
                            className="w-auto min-w-0"
                            portal={true}
                        />
                    </div>
                )}
            </div>

            {/* Right side: Pagination controls & AI Icon spacing */}
            <div className="flex items-center gap-1 pr-[80px]">
                {/* Previous button */}
                <motion.button 
                    whileHover={!(isCursorMode ? !hasPrevPage : currentPage === 1) ? { scale: 1.1 } : {}}
                    whileTap={!(isCursorMode ? !hasPrevPage : currentPage === 1) ? { scale: 0.9, x: -2 } : {}}
                    disabled={isCursorMode ? !hasPrevPage : currentPage === 1}
                    onClick={() => {
                        if (isCursorMode && onPrevCursor) {
                            onPrevCursor();
                        } else if (onPageChange) {
                            onPageChange(currentPage - 1);
                        }
                    }}
                    className={`flex items-center justify-center bg-transparent border-none w-7 h-7 rounded-md text-[color:var(--text-primary)] ${(isCursorMode ? !hasPrevPage : currentPage === 1) ? 'cursor-not-allowed opacity-30' : 'cursor-pointer opacity-80'}`}
                >
                    <ObliqueIcon name="chevron_left" size={18} />
                </motion.button>

                {/* Page numbers (only in page-number mode) */}
                {!isCursorMode && (
                    <div className="flex items-center gap-[3px]">
                        {visiblePages.map((page, idx) => {
                            const isPrevBreak = idx > 0 && page - visiblePages[idx-1] > 1;
                            return (
                                <React.Fragment key={page}>
                                {isPrevBreak && (
                                    <span className="text-[color:var(--text-tertiary)] mx-0.5 text-[10px]">......</span>
                                )}
                                <button
                                    onClick={() => onPageChange(page)}
                                    className={`h-6 rounded flex items-center justify-center text-xs cursor-pointer transition-all duration-200 px-1.5 ${page === totalPages && totalPages > 6 && isPrevBreak ? 'min-w-auto' : 'min-w-[24px]'} ${currentPage === page ? 'border border-[color:var(--border)] bg-[color:var(--bg-subtle)] text-[color:var(--text-primary)] font-medium' : 'border border-transparent bg-transparent text-[color:var(--text-secondary)] font-medium hover:text-[color:var(--text-primary)] hover:bg-[color:var(--bg-subtle)]/50'}`}
                                >
                                    {page === totalPages && totalPages > 6 && isPrevBreak ? 'last Page' : page}
                                </button>
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}

                {/* Cursor mode: current page indicator */}
                {isCursorMode && (
                    <div className="flex items-center gap-1 mx-2">
                        <span className="font-semibold text-[color:var(--text-primary)] text-xs">{currentPage}</span>
                    </div>
                )}

                {/* Next button */}
                <motion.button 
                    whileHover={!(isCursorMode ? !hasNextPage : currentPage === Math.max(1, totalPages)) ? { scale: 1.1 } : {}}
                    whileTap={!(isCursorMode ? !hasNextPage : currentPage === Math.max(1, totalPages)) ? { scale: 0.9, x: 2 } : {}}
                    disabled={isCursorMode ? !hasNextPage : currentPage === Math.max(1, totalPages)}
                    onClick={() => {
                        if (isCursorMode && onNextCursor) {
                            onNextCursor();
                        } else if (onPageChange) {
                            onPageChange(currentPage + 1);
                        }
                    }}
                    className={`flex items-center justify-center bg-transparent border-none w-7 h-7 rounded-md text-[color:var(--text-primary)] ${(isCursorMode ? !hasNextPage : currentPage === Math.max(1, totalPages)) ? 'cursor-not-allowed opacity-30' : 'cursor-pointer opacity-80'}`}
                >
                    <ObliqueIcon name="chevron_right" size={18} />
                </motion.button>
            </div>
        </div>
    );
}
